import { RouteProp, useNavigation, useRoute } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import { Button, Subtitle1, useAlert, useTheme, VectorIcon } from "capsule"
import React, { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Clipboard, StyleSheet, TouchableOpacity, View } from "react-native"
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view"

import { PractitionerHomeNS } from "../../features/i18n/constants"
import { ICodesParamList } from "../MainScreen/MainTabs"

const CHECK_ICON_SIZE = 40
const CHECK_CONTAINER_SIZE = 70
const SUCCESS_GREEN = "#4CAF50"
const CODE_DISPLAY_SIZE = 24
const COPY_ICON_SIZE = 20

type NavigationProp = StackNavigationProp<ICodesParamList>
type RoutePropType = RouteProp<ICodesParamList, "PrescriptionLCAConfirmation">

const PrescriptionLCAConfirmationScreen: FC = () => {
  const {
    fontMaker,
    colors: {
      surface,
      primary,
      white: { highEmphasis: white },
    },
    dimensions: { spacing },
    screenStyle,
  } = useTheme()
  const { t } = useTranslation()
  const { showSnack } = useAlert()
  const navigation = useNavigation<NavigationProp>()
  const route = useRoute<RoutePropType>()
  const generatedCode = route.params?.code || ""

  const handleCopyCode = () => {
    Clipboard.setString(generatedCode)
    showSnack({ message: t("common:button.copied") || "Code copié !" })
  }

  const s = useMemo(
    () => ({
      content: [
        styles.content,
        {
          paddingTop: spacing,
          backgroundColor: surface.background,
        },
      ],
      innerContent: {
        paddingBottom: spacing * 2,
      },
      container: {
        paddingHorizontal: spacing,
        backgroundColor: surface.background,
        alignItems: "center" as const,
      },
      checkContainer: [
        styles.checkContainer,
        {
          marginBottom: spacing * 1.5,
        },
      ],
      messageText: [
        fontMaker({ weight: "Regular" }),
        {
          marginBottom: spacing * 2,
          textAlign: "center" as const,
        },
      ],
      codeContainer: [
        styles.codeContainer,
        {
          backgroundColor: surface.textInput,
          padding: spacing,
          marginBottom: spacing * 2,
          borderRadius: spacing / 2,
        },
      ],
      codeText: [
        fontMaker({ weight: "Bold" }),
        {
          fontSize: CODE_DISPLAY_SIZE,
          color: primary,
        },
      ],
      copyIconContainer: {
        marginLeft: spacing,
        padding: spacing / 2,
      },
      questionText: [
        fontMaker({ weight: "Bold" }),
        {
          marginBottom: spacing,
          textAlign: "center" as const,
        },
      ],
      buttonsContainer: {
        flexDirection: "row" as const,
        justifyContent: "space-between" as const,
        width: "100%",
        marginTop: spacing,
      },
      button: {
        flex: 1,
        marginHorizontal: spacing / 2,
      },
    }),
    [spacing, surface.textInput, surface.background, fontMaker, primary],
  )

  const handleYesPress = () => {
    // Pop back to Codes_Main, then navigate to PrescriptionLCA to reset the form
    navigation.popToTop()
    navigation.navigate("PrescriptionLCA")
  }

  const handleNoPress = () => {
    // Navigate back to Codes_Main screen
    navigation.navigate("Codes_Main")
  }

  return (
    <KeyboardAwareScrollView
      style={[s.content, screenStyle]}
      enableOnAndroid
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={s.innerContent}
    >
      <View style={s.container}>
        <View style={s.checkContainer}>
          <VectorIcon name="check" category="MaterialIcons" size={CHECK_ICON_SIZE} color={white} />
        </View>

        <Subtitle1 emphasis="high" style={s.messageText}>
          {t(`${PractitionerHomeNS}:prescriptionLCA.confirmation.message`)}
        </Subtitle1>

        <View style={s.codeContainer}>
          <Subtitle1 style={s.codeText}>{generatedCode}</Subtitle1>
          <TouchableOpacity onPress={handleCopyCode} style={s.copyIconContainer}>
            <VectorIcon
              name="content-copy"
              category="MaterialIcons"
              size={COPY_ICON_SIZE}
              color={primary}
            />
          </TouchableOpacity>
        </View>

        <Subtitle1 emphasis="high" style={s.questionText}>
          {t(`${PractitionerHomeNS}:prescriptionLCA.confirmation.question`)}
        </Subtitle1>

        <View style={s.buttonsContainer}>
          <Button style={s.button} onPress={handleYesPress}>
            {t(`${PractitionerHomeNS}:prescriptionLCA.confirmation.yes`)}
          </Button>
          <Button
            style={s.button}
            onPress={handleNoPress}
            mode="outlined"
            labelStyle={{ color: primary }}
          >
            {t(`${PractitionerHomeNS}:prescriptionLCA.confirmation.no`)}
          </Button>
        </View>
      </View>
    </KeyboardAwareScrollView>
  )
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  container: {
    alignItems: "center",
  },
  checkContainer: {
    width: CHECK_CONTAINER_SIZE,
    height: CHECK_CONTAINER_SIZE,
    borderRadius: CHECK_CONTAINER_SIZE / 2,
    backgroundColor: SUCCESS_GREEN,
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "center",
  },
  codeContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
})

export default PrescriptionLCAConfirmationScreen
